import React, { useEffect, useState } from 'react';
import {
  LoadingScreen,
  DateDisplay,
  QuoteCard,
  Controls,
  AmbientPlayer,
  Attribution,
  Toast
} from '@/components';
import { StickyNote } from '@/components/StickyNote';
import { useDailyContent } from '@/hooks/useDailyContent';
import { useKeyboard } from '@/hooks/useKeyboard';
import { useShare } from '@/hooks/useShare';

const App: React.FC = () => {
  const { content, dateInfo, isLoading, error, refreshContent } = useDailyContent();
  const { share, isSharing, shareMessage } = useShare();
  const [showLoading, setShowLoading] = useState(true);

  // 键盘快捷键
  useKeyboard({
    onRefresh: refreshContent,
    onShare: () => content?.quote && share(content.quote),
    enabled: !isLoading && !isSharing
  });

  // 控制加载屏幕显示
  useEffect(() => {
    if (!isLoading && content) {
      // 延迟隐藏加载屏幕，确保背景图片加载完成
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, content]);

  // 更新背景图片
  useEffect(() => {
    if (content?.image.url) {
      document.body.style.backgroundImage = `url('${content.image.url}')`;
    }
  }, [content?.image.url]);

  // 处理分享
  const handleShare = () => {
    if (content?.quote) {
      share(content.quote);
    }
  };

  // 错误处理
  if (error && !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center text-white p-8">
          <h2 className="text-2xl font-light mb-4">加载失败</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            onClick={refreshContent}
            className="glass-effect px-6 py-3 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* 加载屏幕 */}
      <LoadingScreen isVisible={showLoading} />

      {/* 主内容 */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* 日期显示 */}
        <DateDisplay dateInfo={dateInfo} />

        {/* 诗词卡片 */}
        <main className="flex-1 flex items-center justify-center px-4">
          {content ? (
            <QuoteCard quote={content.quote} isLoading={isLoading} />
          ) : (
            <div className="container max-w-4xl mx-auto">
              <div className="glass-effect rounded-3xl p-8 md:p-12 text-center animate-pulse">
                <div className="h-8 bg-white bg-opacity-20 rounded mb-6"></div>
                <div className="h-6 bg-white bg-opacity-15 rounded w-3/4 mx-auto"></div>
              </div>
            </div>
          )}
        </main>

        {/* 控制按钮 */}
        <Controls
          onRefresh={refreshContent}
          onShare={handleShare}
          isLoading={isLoading}
        />
      </div>

      {/* 环境音播放器 */}
      <AmbientPlayer />

      {/* 每日专注便签 */}
      <StickyNote />

      {/* 图片署名 */}
      {content?.image && <Attribution image={content.image} />}

      {/* Toast 消息 */}
      <Toast
        message={shareMessage}
        type={shareMessage?.includes('失败') ? 'error' : 'success'}
      />
    </div>
  );
};

export default App;